import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Child } from '@/components/deals/ChildrenList';
import type { USEntry } from '@/components/deals/USEntriesList';

// Fields armazenados em contact_immigration_data (tabela dedicada) em vez
// das colunas deprecadas de contacts. Se adicionar campo novo, atualizar
// aqui pra roteamento correto de leitura/escrita.
const IMMIGRATION_FIELDS = [
  'us_entry_method',
  'us_first_entry_month',
  'us_first_entry_year',
  'us_entry_count',
  'us_entries_data',
  'had_visa_denied',
  'has_ssn',
  'has_ead',
  'ead_status',
  'has_itin',
  'has_drivers_license',
  'in_removal_proceedings',
  'received_nta',
  'has_deportation_order',
  'was_previously_deported',
  'previous_deportation_month',
  'previous_deportation_year',
  'has_ice_checkin',
  'ice_checkin_timeline',
  'spouse_immigration_status',
] as const;

type ImmigrationField = typeof IMMIGRATION_FIELDS[number];

function splitImmigrationFields<T extends Record<string, unknown>>(
  data: T,
): { contactFields: Partial<T>; immigrationFields: Partial<Record<ImmigrationField, unknown>> } {
  const immigrationFields: Partial<Record<ImmigrationField, unknown>> = {};
  const contactFields: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    if ((IMMIGRATION_FIELDS as readonly string[]).includes(key)) {
      immigrationFields[key as ImmigrationField] = value;
    } else {
      contactFields[key] = value;
    }
  }

  return { contactFields: contactFields as Partial<T>, immigrationFields };
}

interface ContactQualificationData {
  // Personal data
  gender: string | null;
  language: string | null;
  
  // Location data (from primary_address)
  address_city: string | null;
  address_state: string | null;
  address_country_code: string | null;
  primary_address: string | null;
  
  // Entry to USA (updated for month/year)
  us_entry_method: string | null;
  us_first_entry_month: number | null;
  us_first_entry_year: number | null;
  us_entry_count: string | null; // Changed to string for dropdown
  us_entries_data: USEntry[]; // NEW - JSONB list of entries
  had_visa_denied: boolean;
  
  // Documents (EAD simplified - no expiration date)
  has_ssn: boolean;
  has_ead: boolean;
  ead_status: string | null;
  has_itin: boolean;
  has_drivers_license: boolean;
  
  // Removal proceedings (updated for month/year and timeline)
  in_removal_proceedings: boolean;
  received_nta: boolean;
  has_deportation_order: boolean;
  was_previously_deported: boolean;
  previous_deportation_month: number | null;
  previous_deportation_year: number | null;
  has_ice_checkin: boolean;
  ice_checkin_timeline: string | null; // NEW - relative timeline
  
  // Family
  marital_status: string | null;
  spouse_immigration_status: string | null;
  has_children: boolean;
  children_data: Child[]; // JSONB
}

interface ImmigrationProcess {
  id?: string;
  contact_id: string;
  process_type: string;
  process_status: string | null;
  filed_year: number | null;
  notes: string | null;
}

interface DealQualificationData {
  id?: string;
  deal_id: string;
  current_immigration_status: string | null;
  objective: string | null;
  urgency: string | null;
  urgency_reason: string | null;
  // budget_range removed from UI
  has_previous_lawyer: boolean;
  had_lawyer_problems: boolean;
  lawyer_problem_details: string | null;
  has_active_lawyer: boolean;
  active_lawyer_name: string | null;
  reason_seeking_new_lawyer: string | null;
  reason_seeking_new_lawyer_details: string | null;
  qualification_notes: string | null;
  qualified_at: string | null;
  qualified_by: string | null;
}

export function useQualification(dealId: string, contactId: string | null) {
  const [contactData, setContactData] = useState<ContactQualificationData | null>(null);
  const [immigrationProcesses, setImmigrationProcesses] = useState<ImmigrationProcess[]>([]);
  const [dealQualification, setDealQualification] = useState<DealQualificationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load all qualification data
  const loadData = useCallback(async () => {
    if (!dealId) return;
    
    setLoading(true);
    try {
      // Load contact qualification fields
      if (contactId) {
        // Non-immigration fields ainda vivem em contacts (has_children mantido como
        // intent do usuario — array em si vive em contact_children)
        const { data: contact } = await supabase
          .from('contacts')
          .select(`
            gender, language, primary_address,
            marital_status, has_children
          `)
          .eq('id', contactId)
          .single();

        // Immigration fields vivem em contact_immigration_data (pode nao existir ainda)
        const { data: immigration } = await supabase
          .from('contact_immigration_data')
          .select(`
            us_entry_method, us_first_entry_month, us_first_entry_year, us_entry_count,
            us_entries_data, had_visa_denied,
            has_ssn, has_ead, ead_status, has_itin, has_drivers_license,
            in_removal_proceedings, received_nta, has_deportation_order, was_previously_deported,
            previous_deportation_month, previous_deportation_year, has_ice_checkin, ice_checkin_timeline,
            spouse_immigration_status
          `)
          .eq('contact_id', contactId)
          .maybeSingle();

        // Children vivem em contact_children (normalizado em linhas)
        const { data: childrenRows } = await supabase
          .from('contact_children')
          .select('age, status, lives_with')
          .eq('contact_id', contactId)
          .order('created_at', { ascending: true });

        if (contact) {
          const immigrationData = immigration ?? {};
          const childrenData: Child[] = (childrenRows ?? []).map((row) => ({
            age: row.age ?? 0,
            status: (row.status ?? 'unknown') as Child['status'],
            lives_with: row.lives_with ?? true,
          }));
          // Parse JSONB fields if they're strings
          const parsedContact: any = {
            ...contact,
            ...immigrationData,
            us_entry_count: (immigrationData as any).us_entry_count?.toString() || null,
            children_data: childrenData,
            us_entries_data: (immigrationData as any).us_entries_data
              ? (typeof (immigrationData as any).us_entries_data === 'string'
                  ? JSON.parse((immigrationData as any).us_entries_data)
                  : (immigrationData as any).us_entries_data)
              : [],
            address_city: null,
            address_state: null,
            address_country_code: null,
          };

          // Load primary address if exists
          if (contact.primary_address) {
            const { data: address } = await supabase
              .from('contact_addresses')
              .select('city, state, country_code')
              .eq('id', contact.primary_address)
              .single();

            if (address) {
              parsedContact.address_city = address.city;
              parsedContact.address_state = address.state;
              parsedContact.address_country_code = address.country_code;
            }
          }

          setContactData(parsedContact as ContactQualificationData);
        }

        // Load immigration processes
        const { data: processes } = await supabase
          .from('contact_immigration_processes')
          .select('*')
          .eq('contact_id', contactId)
          .order('created_at', { ascending: true });
        
        setImmigrationProcesses(processes || []);
      }

      // Load deal qualification
      const { data: qualification } = await supabase
        .from('deal_qualification')
        .select('*')
        .eq('deal_id', dealId)
        .maybeSingle();
      
      setDealQualification(qualification as DealQualificationData | null);
    } catch (error) {
      console.error('Error loading qualification data:', error);
    } finally {
      setLoading(false);
    }
  }, [dealId, contactId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Save contact qualification data
  const saveContactData = async (data: Partial<ContactQualificationData>) => {
    if (!contactId) return;

    setSaving(true);
    try {
      // Separate address fields from contact fields
      const { address_city, address_state, address_country_code, primary_address, ...rest } = data as any;

      // children_data vai pra tabela dedicada contact_children (sincronizada por
      // delete-then-insert — array geralmente pequeno)
      const { children_data: childrenUpdate, ...withoutChildren } = rest;

      // Roteia: fields de imigracao vao pra contact_immigration_data,
      // os demais ficam em contacts.
      const { contactFields, immigrationFields } = splitImmigrationFields(withoutChildren);

      // Convert us_entry_count to number for storage if present
      const immigrationToSave = { ...immigrationFields } as any;
      if (immigrationToSave.us_entry_count !== undefined) {
        const count = immigrationToSave.us_entry_count;
        immigrationToSave.us_entry_count = count === '5+' ? 5 : (count ? parseInt(count) : null);
      }

      // Save non-immigration contact fields
      if (Object.keys(contactFields).length > 0) {
        const { error } = await supabase
          .from('contacts')
          .update(contactFields)
          .eq('id', contactId);

        if (error) throw error;
      }

      // Upsert immigration fields na tabela dedicada (pode ser primeira vez pra este contato)
      if (Object.keys(immigrationToSave).length > 0) {
        const { error: immigrationError } = await supabase
          .from('contact_immigration_data')
          .upsert(
            { contact_id: contactId, ...immigrationToSave },
            { onConflict: 'contact_id' },
          );

        if (immigrationError) throw immigrationError;
      }

      // Sincroniza contact_children se o array foi passado no update
      if (childrenUpdate !== undefined) {
        const newChildren = Array.isArray(childrenUpdate) ? childrenUpdate : [];

        const { error: deleteError } = await supabase
          .from('contact_children')
          .delete()
          .eq('contact_id', contactId);

        if (deleteError) throw deleteError;

        if (newChildren.length > 0) {
          const rowsToInsert = newChildren.map((child: Child) => ({
            contact_id: contactId,
            age: child.age,
            status: child.status,
            lives_with: child.lives_with,
          }));

          const { error: insertError } = await supabase
            .from('contact_children')
            .insert(rowsToInsert);

          if (insertError) throw insertError;
        }
      }
      
      // Handle address fields if any are present
      if (address_city !== undefined || address_state !== undefined || address_country_code !== undefined) {
        const currentPrimaryAddress = contactData?.primary_address;
        
        if (currentPrimaryAddress) {
          // Update existing address
          await supabase
            .from('contact_addresses')
            .update({
              city: address_city ?? null,
              state: address_state ?? null,
              country_code: address_country_code ?? null,
            })
            .eq('id', currentPrimaryAddress);
        } else {
          // Create new address and set as primary
          const { data: newAddress } = await supabase
            .from('contact_addresses')
            .insert({
              contact_id: contactId,
              city: address_city ?? null,
              state: address_state ?? null,
              country_code: address_country_code ?? null,
              type: 'current',
            })
            .select('id')
            .single();
          
          if (newAddress) {
            await supabase
              .from('contacts')
              .update({ primary_address: newAddress.id })
              .eq('id', contactId);
          }
        }
      }
      
      setContactData(prev => prev ? { ...prev, ...data } : null);
      toast.success('Dados do contato salvos');
    } catch (error: any) {
      console.error('=== ERRO AO SALVAR DADOS DO CONTATO ===');
      console.error('Erro completo:', error);
      console.error('Mensagem:', error?.message);
      console.error('Código:', error?.code);
      console.error('Detalhes:', error?.details);
      console.error('Hint:', error?.hint);
      toast.error('Erro ao salvar dados do contato');
    } finally {
      setSaving(false);
    }
  };

  // Add/update immigration process
  const saveImmigrationProcess = async (process: Omit<ImmigrationProcess, 'id'> & { id?: string }) => {
    if (!contactId) return;
    
    setSaving(true);
    try {
      if (process.id) {
        // Update existing
        const { error } = await supabase
          .from('contact_immigration_processes')
          .update({
            process_type: process.process_type,
            process_status: process.process_status,
            filed_year: process.filed_year,
            notes: process.notes,
          })
          .eq('id', process.id);
        
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('contact_immigration_processes')
          .upsert({
            contact_id: contactId,
            process_type: process.process_type,
            process_status: process.process_status,
            filed_year: process.filed_year,
            notes: process.notes,
          }, {
            onConflict: 'contact_id,process_type'
          });
        
        if (error) throw error;
      }
      
      await loadData();
      toast.success('Processo imigratório salvo');
    } catch (error) {
      console.error('Error saving immigration process:', error);
      toast.error('Erro ao salvar processo imigratório');
    } finally {
      setSaving(false);
    }
  };

  // Delete immigration process
  const deleteImmigrationProcess = async (processId: string) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('contact_immigration_processes')
        .delete()
        .eq('id', processId);
      
      if (error) throw error;
      
      setImmigrationProcesses(prev => prev.filter(p => p.id !== processId));
      toast.success('Processo removido');
    } catch (error) {
      console.error('Error deleting immigration process:', error);
      toast.error('Erro ao remover processo');
    } finally {
      setSaving(false);
    }
  };

  // Save deal qualification
  const saveDealQualification = async (data: Partial<DealQualificationData>) => {
    setSaving(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user?.id)
        .single();

      const qualificationData = {
        deal_id: dealId,
        ...data,
        qualified_at: data.qualified_at || new Date().toISOString(),
        qualified_by: appUser?.id || null,
      };

      if (dealQualification?.id) {
        // Update existing
        const { error } = await supabase
          .from('deal_qualification')
          .update(qualificationData)
          .eq('id', dealQualification.id);
        
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('deal_qualification')
          .insert(qualificationData);
        
        if (error) throw error;
      }
      
      await loadData();
      toast.success('Qualificação salva');
    } catch (error: any) {
      console.error('=== ERRO AO SALVAR QUALIFICAÇÃO ===');
      console.error('Erro completo:', error);
      console.error('Mensagem:', error?.message);
      console.error('Código:', error?.code);
      console.error('Detalhes:', error?.details);
      console.error('Hint:', error?.hint);
      toast.error(`Erro ao salvar qualificação: ${error?.message || 'desconhecido'}`);
    } finally {
      setSaving(false);
    }
  };

  return {
    contactData,
    immigrationProcesses,
    dealQualification,
    loading,
    saving,
    saveContactData,
    saveImmigrationProcess,
    deleteImmigrationProcess,
    saveDealQualification,
    reload: loadData,
  };
}
